class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    this.__clxSmartRecommendationsCaptured = false;
    this.__clxSmartRecommendations = null;
    this.__clxSmartRefreshPromise = null;

    this.captureSmartRecommendations(document);
    requestAnimationFrame(() => this.applySmartRecommendations());

    document.addEventListener('clx:cart-updated', () => {
      this.refreshSmartRecommendations();
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  captureSmartRecommendations(source) {
    let scope = source;
    if (typeof source === 'string') {
      scope = new DOMParser().parseFromString(source, 'text/html');
    }
    if (!scope?.querySelector) return false;

    const template = scope.querySelector('#ClxSmartDrawerRecommendations');
    if (!template) return false;

    const templateScope = template.content || template;
    const recommendation = templateScope.querySelector(
      '.clx-drawer-recommendations[data-clx-smart-recommendations="set-metafield-v9"]'
    );

    this.__clxSmartRecommendationsCaptured = true;
    this.__clxSmartRecommendations = recommendation ? recommendation.cloneNode(true) : null;
    return true;
  }

  applySmartRecommendations() {
    const current = this.querySelector('.clx-drawer-recommendations');

    if (!this.__clxSmartRecommendationsCaptured) {
      if (current && !current.matches('[data-clx-smart-recommendations="set-metafield-v9"]')) current.remove();
      return;
    }

    if (!this.__clxSmartRecommendations) {
      current?.remove();
      return;
    }

    const fresh = this.__clxSmartRecommendations.cloneNode(true);
    if (current) {
      current.replaceWith(fresh);
    } else if (!this.classList.contains('is-empty')) {
      const anchor = this.querySelector('.clx-drawer-mobile-actions') || this.querySelector('.drawer__footer');
      if (anchor) anchor.after(fresh);
    }

    window.CLXCartUX?.normalize?.(this);
  }

  refreshSmartRecommendations() {
    if (this.__clxSmartRefreshPromise) return this.__clxSmartRefreshPromise;

    const cartUrl = window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
    this.__clxSmartRefreshPromise = fetch(`${cartUrl}?section_id=cart-drawer`, { cache: 'no-store' })
      .then((response) => response.text())
      .then((html) => {
        if (this.captureSmartRecommendations(html)) this.applySmartRecommendations();
      })
      .catch(() => {
        const current = this.querySelector('.clx-drawer-recommendations');
        if (current && !current.matches('[data-clx-smart-recommendations="set-metafield-v9"]')) current.remove();
      })
      .finally(() => {
        this.__clxSmartRefreshPromise = null;
      });

    return this.__clxSmartRefreshPromise;
  }

  resetScrollPosition() {
    const targets = [
      this.querySelector('cart-drawer-items'),
      this.querySelector('.drawer__inner'),
      this.querySelector('.drawer__inner-empty'),
    ];
    targets.forEach((element) => {
      if (!element) return;
      element.scrollTop = 0;
      element.scrollLeft = 0;
      if (typeof element.scrollTo === 'function') {
        element.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    });
  }

  open(triggeredBy) {
    this.applySmartRecommendations();
    this.refreshSmartRecommendations();
    this.resetScrollPosition();

    if (this.classList.contains('active')) {
      requestAnimationFrame(() => this.resetScrollPosition());
      return;
    }

    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.resetScrollPosition();
      requestAnimationFrame(() => {
        this.applySmartRecommendations();
        this.resetScrollPosition();
        requestAnimationFrame(() => this.resetScrollPosition());
      });
      setTimeout(() => this.resetScrollPosition(), 120);
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
        this.resetScrollPosition();
        requestAnimationFrame(() => this.resetScrollPosition());
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');
    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }
    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });
    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;

    const drawerSection = parsedState.sections?.['cart-drawer'];
    if (drawerSection) this.captureSmartRecommendations(drawerSection);

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement || !parsedState.sections?.[section.id]) return;
      const replacementHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      if (replacementHTML !== null) sectionElement.innerHTML = replacementHTML;
    });

    this.applySmartRecommendations();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.applySmartRecommendations();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return source ? source.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }
}

if (!customElements.get('cart-drawer-items')) customElements.define('cart-drawer-items', CartDrawerItems);

/* CollexyLux CART UX BUILD 2026 — V11: one recommendation engine; drawer consumes the same Liquid output as full cart. */
(() => {
  const current = document.currentScript?.src;
  if (!current) return;

  document.getElementById('ClxSingleMobileCartBag')?.remove();
  const style = document.createElement('style');
  style.id = 'ClxSingleMobileCartBag';
  style.textContent = `
    @media (max-width: 749px) {
      body.clx-floating-bag-active #cart-icon-bubble {
        visibility: hidden !important;
        pointer-events: none !important;
      }
      body:not(.clx-floating-bag-active) #cart-icon-bubble {
        visibility: visible !important;
        pointer-events: auto !important;
      }
      cart-drawer-items { overflow-anchor: none !important; }
    }
    cart-drawer:not(.is-empty) .clx-drawer-recommendations:not([data-clx-smart-recommendations="set-metafield-v9"]) {
      display: none !important;
    }
  `;
  document.head.appendChild(style);

  const url = new URL(current);
  const base = url.href.slice(0, url.href.lastIndexOf('/') + 1);
  const files = ['clx-cart-ux-core-20260831-v5.js'];

  const load = (index) => {
    if (index >= files.length) return;
    const script = document.createElement('script');
    script.src = `${base}${files[index]}`;
    script.dataset.clxCartUx = 'v11';
    script.onload = () => load(index + 1);
    document.head.appendChild(script);
  };

  load(0);
})();
